import Button from './Button';
import { Link } from './Link';
import { cx } from './shared';
import { StatusNotice } from './StatusNotice';
import {
  getStatePanelPresentation,
  type StatePanelProps,
} from './statePanelContract';

const recoveryVariant = (state: StatePanelProps['state']) => {
  if (state === 'failed' || state === 'blocked') return 'caution';
  if (state === 'denied' || state === 'unavailable') return 'secondary';
  return 'primary';
};

const RecoveryControl = ({
  props,
  label,
}: {
  props: StatePanelProps;
  label: string;
}) => {
  const recovery = props.recovery;
  if (!recovery) return null;

  if (typeof recovery.href === 'string') {
    return (
      <Link
        href={recovery.href}
        isDisabled={recovery.disabled}
        data-recovery-kind={recovery.kind}
        variant="neutral"
      >
        {label}
      </Link>
    );
  }

  return (
    <Button
      variant={recoveryVariant(props.state)}
      disabled={recovery.disabled}
      isLoading={recovery.isLoading}
      onClick={recovery.onAction}
      data-recovery-kind={recovery.kind}
    >
      {label}
    </Button>
  );
};

export function StatePanel(props: StatePanelProps) {
  const presentation = getStatePanelPresentation(props);

  return (
    <div
      data-so-state-panel={props.state}
      aria-busy={props.state === 'loading' || undefined}
      className={cx('min-w-0', props.className)}
    >
      <StatusNotice
        state={props.state}
        title={presentation.title}
        titleAs="h3"
        announce={props.announce ?? true}
        technicalDetails={props.technicalDetails}
        description={
          <div className="grid gap-4">
            <p className="max-w-reading">{presentation.description}</p>
            <dl className="grid gap-3 border-t border-current/30 pt-3 sm:grid-cols-2">
              {presentation.context.map((item) => (
                <div key={item.label} className="min-w-0">
                  <dt className="text-meta font-semibold uppercase tracking-wide">
                    {item.label}
                  </dt>
                  <dd
                    className={cx(
                      'mt-1 break-words text-body font-medium',
                      item.label === 'Observed at' && 'so-data-text',
                    )}
                  >
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
            {props.additionalContext ? (
              <div className="border-t border-current/30 pt-3">
                {props.additionalContext}
              </div>
            ) : null}
          </div>
        }
        action={
          presentation.recoveryLabel ? (
            <RecoveryControl props={props} label={presentation.recoveryLabel} />
          ) : undefined
        }
      />
    </div>
  );
}

export default StatePanel;
